
import { useContext, useState } from "react"
import { Divider, ReadOnlyBadge } from "@components"
import { NotificationContext, UserContext } from "@context"
import { hasExpired, mySqlToLocal } from "@helpers"
import { useUpdateEvent } from "@hooks"
import { Input, Textarea } from "@heroui/react"
import { Button } from "@heroui/react"

import PropTypes from "prop-types"

const EventBasicInfo = ({ backButton, event }) => {
  const initialFormData = {
    title: event.title,
    description: event.description,
  }

  /**
   * State
   */
  const [form, setForm] = useState({ ...initialFormData })
  const [savedFormData, setSavedFormData] = useState({ ...initialFormData })

  /**
   * Context
   */
  const { notify } = useContext(NotificationContext)
  const { setCurrentEvent } = useContext(UserContext)

  /**
   * Hooks
   */
  const {updateEvent, loading} = useUpdateEvent()

  /**
   * Functions
   */
  const handleSave = () => {
    updateEvent({ 
      variables: {
        input: {
          id: event.id,
          title: form.title,
          description: form.description,
        }
      },
      
      onCompleted: (response) => {
        notify({ title: "Success", type: "success", content: `Event updated successfully.`, open: true })
        setCurrentEvent({ ...event, title: form.title, description: form.description })
        setSavedFormData({ ...form })
      },

      onError: (error) => {
        notify({ title: "Error", type: "danger", content: `An error occurred while updating the event.`, open: true })
      }
    })
  }

  const isTitleValid = () => form.title.length >= 3 && form.title.length <= 255
  const isDescriptionValid = () => form.description.length >= 25 && form.description.length <= 2000
  const isFormValid = () => isTitleValid() && isDescriptionValid()
  
  /**
   * Rendering
   */
  const isDirty = savedFormData.title !== form.title || savedFormData.description !== form.description
  const isReadOnlyMode = hasExpired(mySqlToLocal(event.end_date))
  const isSaveDisabled = !isDirty || !isFormValid()

  return (
    <div>
      <div className="flex justify-end">
        {isReadOnlyMode && <ReadOnlyBadge className="mb-4" isFullWidth size="lg" />}
      </div>

      <form className="flex flex-col mb-6 gap-y-4" noValidate>
        <Input
          autoFocus
          isRequired
          type="text"
          label="Event Title"
          placeholder="Enter a title for your event"
          labelPlacement="outside"
          value={form.title}
          onValueChange={(value) => setForm({ ...form, title: value })}
          minLength={3}
          maxLength={255}
          isInvalid={!isTitleValid()}
          errorMessage="You must supply a title between 3 and 255 characters."
          isDisabled={isReadOnlyMode}
        />

        <Divider />

        <Textarea
          isRequired
          label="Description"
          labelPlacement="outside"
          minRows={20}
          placeholder="Enter your description"
          value={form.description}
          onValueChange={(value) => setForm({ ...form, description: value })}
          minLength={25}
          maxLength={2000}
          isInvalid={!isDescriptionValid()}
          errorMessage="You must supply a short description between 25 and 2000 characters."
          isDisabled={isReadOnlyMode}
        />
      </form>

      <Button className="font-semibold" color="secondary" isDisabled={isSaveDisabled} fullWidth onPress={() => handleSave()} isLoading={loading}>Save</Button>
      {backButton}
    </div>
  )
}

EventBasicInfo.propTypes = {
  backButton: PropTypes.element.isRequired,
  event: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }).isRequired,
}

export default EventBasicInfo